import { JsonFieldType, jsonSchema } from './schema';
import { JsonSchema, FormData, FieldTypeToValueType, JsonField } from './types';

// For usage in collector to validate the data we get from API
export const fetchDataFromJson = (data: unknown): JsonSchema => {
  return jsonSchema.parse(data);
};

const getInitialData = (
  field: JsonField
): FieldTypeToValueType[JsonFieldType] => {
  if (typeof field.default !== 'undefined') {
    return field.default;
  }

  switch (field.type) {
    case JsonFieldType.Checkbox:
      return false;
    case JsonFieldType.Text:
      return '';
    case JsonFieldType.Select:
      return field.options[0]?.id ?? '';
    default:
      return '';
  }
};

export const buildFormData = (data: JsonSchema): FormData => {
  return data.fields.reduce(
    (acc, field) => ({
      ...acc,
      [field.key]: getInitialData(field),
    }),
    {}
  );
};

export const resetAdvancedFields = (
  data: FormData,
  schema: JsonSchema
): FormData => {
  return Object.entries(data).reduce((acc, [key, value]) => {
    const field: JsonField | undefined = schema.fields.find(
      (field) => field.key === key
    );

    if (!field) {
      return {
        ...acc,
        [key]: value,
      };
    }

    return {
      ...acc,
      [key]: field.required ? value : getInitialData(field),
    };
  }, {});
};

export const fieldTypeMatchesValue = <T extends JsonFieldType>(
  fieldType: T,
  value: unknown
): value is FieldTypeToValueType[T] => {
  switch (fieldType) {
    case JsonFieldType.Checkbox:
      return typeof value === 'boolean';
    case JsonFieldType.Text:
    case JsonFieldType.Select:
    default:
      return typeof value === 'string';
  }
};

const isFieldValid = (field: JsonField, value: unknown) => {
  if (field.type === JsonFieldType.Checkbox) {
    return typeof value === 'boolean';
  }

  return !!value;
};

export const validateRequiredFieldByKey = (
  key: string,
  value: unknown,
  schema: JsonSchema
): boolean => {
  const field = schema.fields.find((field) => field.key === key);

  if (!field || !field.required) {
    return true;
  }

  return isFieldValid(field, value);
};
